import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import XIcon from './icons/XIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';
import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';
import CheckCircleIcon from './icons/CheckCircleIcon';
import LassoSelectIcon from './icons/LassoSelectIcon';

const PanToolIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    <path d="M12 21a9 9 0 0 1-9-9 4 4 0 0 1 4-4h10a4 4 0 0 1 4 4 9 9 0 0 1-9 9z" />
  </svg>
);

const EditPointsIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m12 12 4 10 1.7-4.3L22 16z" />
    <path d="m16.5 3.5 2.8 2.8c.4.4.4 1 0 1.4l-4.2 4.2c-.4.4-1 .4-1.4 0L10.8 9c-.4-.4-.4-1 0-1.4l4.2-4.2c.4-.4 1-.4 1.4 0z" />
  </svg>
);

interface MaskCreatorProps {
  imageUrl: string;
  onComplete: (maskedImageDataUrl: string) => void;
  onCancel: () => void;
}

type Point = { x: number; y: number };
type Tool = 'draw' | 'edit' | 'pan';

const MOUSE_POINTER_ICON = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="m13 13 6 6" /></svg>;

const MaskCreator: React.FC<MaskCreatorProps> = ({ imageUrl, onComplete, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [points, setPoints] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
  const [activeTool, setActiveTool] = useState<Tool>('draw');
  const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null);
  const [edgeFeather, setEdgeFeather] = useState(0);
  const [lineDashOffset, setLineDashOffset] = useState(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image.src || image.naturalWidth === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(viewState.x, viewState.y);
    ctx.scale(viewState.scale, viewState.scale);
    ctx.drawImage(image, 0, 0);

    if (points.length > 0) {
      const isFinished = !isDrawing && points.length > 2;

      // Draw Preview Mask
      if (isFinished) {
        ctx.save();
        ctx.fillStyle = 'rgba(20, 184, 166, 0.3)';
        ctx.strokeStyle = 'rgba(20, 184, 166, 1)';
        ctx.lineWidth = 1.5 / viewState.scale;
        ctx.setLineDash([6, 4]);
        ctx.lineDashOffset = -lineDashOffset;

        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      // Draw path outline
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2 / viewState.scale;
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      if (isFinished) ctx.closePath();
      ctx.stroke();

      // Draw points if editing
      if (activeTool === 'edit' && isFinished) {
        points.forEach(p => {
          ctx.fillStyle = '#14b8a6';
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 1.5 / viewState.scale;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5 / viewState.scale, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }
    }
    ctx.restore();
  }, [points, isDrawing, viewState, activeTool, lineDashOffset]);

  // Animation loop for "marching ants"
  useEffect(() => {
    const animateDash = () => {
      setLineDashOffset(offset => (offset + 0.25) % 10);
      animationFrameRef.current = requestAnimationFrame(animateDash);
    };
    animateDash();
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const resetView = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (container && canvas && image.naturalWidth > 0) {
      const { width: cW, height: cH } = container.getBoundingClientRect();
      canvas.width = cW;
      canvas.height = cH;
      const { naturalWidth: iW, naturalHeight: iH } = image;
      const scale = Math.min(cW / iW, cH / iH) * 0.9;
      setViewState({
        scale,
        x: (cW - iW * scale) / 2,
        y: (cH - iH * scale) / 2,
      });
    }
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    image.crossOrigin = "Anonymous";
    image.src = imageUrl;
    image.onload = resetView;
    window.addEventListener('resize', resetView);
    return () => window.removeEventListener('resize', resetView);
  }, [imageUrl, resetView]);

  useEffect(() => {
    requestAnimationFrame(draw);
  }, [draw]);

  const getPointInWorldSpace = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - viewState.x) / viewState.scale,
      y: (e.clientY - rect.top - viewState.y) / viewState.scale,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const worldPos = getPointInWorldSpace(e);
    if (activeTool === 'pan') {
      setIsDrawing(true);
      setViewState(v => ({ ...v, startPan: { x: e.clientX - v.x, y: e.clientY - v.y } } as any));
      return;
    }
    if (activeTool === 'edit' && !isDrawing) {
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const distance = Math.sqrt(Math.pow(p.x - worldPos.x, 2) + Math.pow(p.y - worldPos.y, 2));
        if (distance < 6 / viewState.scale) {
          setDraggingPointIndex(i);
          setIsDrawing(true);
          return;
        }
      }
    }
    if (activeTool === 'draw') {
      setIsDrawing(true);
      if (e.buttons === 1) { // Left click
        setPoints(p => [...p, worldPos]);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing) return;
    const worldPos = getPointInWorldSpace(e);

    if (activeTool === 'pan') {
      const { startPan } = viewState as any;
      setViewState(v => ({ ...v, x: e.clientX - startPan.x, y: e.clientY - startPan.y }));
      return;
    }
    if (activeTool === 'edit' && draggingPointIndex !== null) {
      const newPoints = [...points];
      newPoints[draggingPointIndex] = worldPos;
      setPoints(newPoints);
      return;
    }
    if (activeTool === 'draw') {
      const lastPoint = points[points.length - 1];
      if (!lastPoint) return;
      const distance = Math.sqrt(Math.pow(worldPos.x - lastPoint.x, 2) + Math.pow(worldPos.y - lastPoint.y, 2));
      if (distance > 5 / viewState.scale) {
        setPoints(p => [...p, worldPos]);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setDraggingPointIndex(null);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const scaleAmount = 1.1;
    const delta = e.deltaY > 0 ? 1 / scaleAmount : scaleAmount;
    const newScale = Math.max(0.1, Math.min(viewState.scale * delta, 20));
    const worldX = (mouseX - viewState.x) / viewState.scale;
    const worldY = (mouseY - viewState.y) / viewState.scale;
    const newX = mouseX - worldX * newScale;
    const newY = mouseY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  };

  const handleClear = () => setPoints([]);
  const handleUndo = () => setPoints(p => p.slice(0, -1));

  const handleConfirm = () => {
    if (points.length < 3) return;
    const image = imageRef.current;
    const { naturalWidth: iW, naturalHeight: iH } = image;

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = iW;
    finalCanvas.height = iH;
    const ctx = finalCanvas.getContext('2d');
    if (!ctx) return;

    if (edgeFeather > 0) {
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = iW;
      maskCanvas.height = iH;
      const maskCtx = maskCanvas.getContext('2d');
      if (!maskCtx) return;

      maskCtx.beginPath();
      maskCtx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) maskCtx.lineTo(points[i].x, points[i].y);
      maskCtx.closePath();
      maskCtx.fillStyle = 'white';
      maskCtx.fill();

      maskCtx.filter = `blur(${edgeFeather}px)`;
      maskCtx.globalCompositeOperation = 'destination-in';
      maskCtx.fill();

      ctx.drawImage(image, 0, 0);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(maskCanvas, 0, 0);

    } else {
      ctx.drawImage(image, 0, 0);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.closePath();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fill();
    }

    onComplete(finalCanvas.toDataURL('image/png'));
  };

  const isComplete = !isDrawing && points.length > 2;

  const toolConfig = {
    draw: { cursor: 'crosshair', icon: <LassoSelectIcon className="w-5 h-5" />, name: 'Draw/Select' },
    edit: { cursor: 'pointer', icon: <EditPointsIcon className="w-5 h-5" />, name: 'Edit Points' },
    pan: { cursor: 'grab', icon: <PanToolIcon className="w-5 h-5" />, name: 'Pan & Zoom' },
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zinc-950"
    >
      {/* Header/Instructions */}
      <motion.div
        initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-20 w-full max-w-lg p-3 bg-zinc-950/70 backdrop-blur-md rounded-xl border border-white/10 flex justify-between items-center"
      >
        <div className="flex items-center gap-3">
          {MOUSE_POINTER_ICON}
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-white">Click to add points, drag for freehand.</span> Use tools to refine.
          </p>
        </div>
        <button onClick={onCancel} className="p-1.5 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-5 h-5" /></button>
      </motion.div>

      {/* Main Canvas Area */}
      <div ref={containerRef} className="w-full h-full" onWheel={handleWheel} >
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ cursor: isDrawing && activeTool === 'pan' ? 'grabbing' : toolConfig[activeTool].cursor }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
        />
      </div>

      {/* Toolbar */}
      <motion.div
        initial={{ x: -80, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.5 }}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-2 p-2 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl"
      >
        {(Object.keys(toolConfig) as Tool[]).map(tool => (
          <button
            key={tool} onClick={() => setActiveTool(tool)} title={toolConfig[tool].name}
            className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-colors ${activeTool === tool ? 'text-teal-400' : 'text-zinc-300 hover:bg-white/10'}`}
          >
            {activeTool === tool && <motion.div layoutId="tool-active" className="absolute inset-0 bg-white/10 rounded-full" />}
            <span className="relative z-10">{toolConfig[tool].icon}</span>
          </button>
        ))}
      </motion.div>

      {/* Action Panel */}
      <motion.div
        initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 p-2 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl"
      >
        <button onClick={handleUndo} disabled={points.length === 0} className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 disabled:opacity-50 transition-colors">Undo</button>
        <button onClick={handleClear} disabled={points.length === 0} className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 disabled:opacity-50 transition-colors"><RefreshCwIcon className="w-5 h-5" /></button>
        <div className="w-px h-6 bg-white/10 mx-2" />
        <div className="flex items-center gap-2 px-2">
          <label className="text-xs font-semibold text-zinc-400">Feather:</label>
          <input type="range" min="0" max="10" step="0.5" value={edgeFeather} onChange={e => setEdgeFeather(Number(e.target.value))} className="w-20" />
        </div>
        <div className="w-px h-6 bg-white/10 mx-2" />
        <button onClick={handleConfirm} disabled={!isComplete} className="flex items-center gap-2 pl-4 pr-5 py-2.5 rounded-full text-sm font-semibold bg-teal-500 text-white disabled:bg-zinc-700 disabled:text-zinc-400 transition-colors">
          <CheckCircleIcon className="w-5 h-5" /> Apply Mask
        </button>
      </motion.div>
    </motion.div>
  );
};
export default MaskCreator;
